/**
  * @class meleItem
  * @description
  *
  * # Purpose
  * Implements `<mele-item>` as a clickable item element with text and/or icon.
  *
  * # Use
  * Create element in HTML:
  * ```
  * parentEl.innerHTML = '<mele-item label="My Item"></mele-item>>';
  * ```
  * Create element in JavaScript:
  * ```
  * var el = elParent.appendChildren({
  *   tag: mele-item',
  *   label: 'country.SK',
  *   iconleft: 'plain_clear',
  * });
  * ```
  * Programmatically invoke click:
  * ```
  * el.click();
  * ```
  *
  * # Implementation notes
  * When clicked and not disabled, sends mmItemClicked to Mosy module where the tag belongs
  */

import meleButton from 'meleButton'
const RE_COUNT_INDENTS = />./g

const PROPERTIES = _.extend({}, {
  new: { description: 'value indicating a new item', type: 'boolean' },
  value: { description: 'value for automatic filling of attributes', type: 'string' },
  attachments: { description: 'additional value for automatic filling of attributes', type: 'string' },
  collapsed: { description: 'indicates if item is collapsed', type: 'boolean' },
  indent: { description: 'indicates level of indentation for items containing > character in key', type: 'number', readonly: true },
  data: { description: 'internal data for the item', type: 'object', readonly: true, noattribute: true },
  sorting: { type: 'integer', description: "item order. NOTE: name of the property is not 'order' due to collisions with sql" },
  removable: { description: 'whether to display remove icon', type: 'boolean' },
  indenticon: { type: 'string', description: "set which icon should be used for indented items. Exactly two items are necessary for this to work properly, separated by ','" },
}, meleButton._properties)

export default class meleItem extends meleButton {
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
   * Handle change of attribute "key"
   */
  keyChanged(value) {
    super.keyChanged(value)
    // collapsed icon is displayed when key ends with >
    // so we have to refresh collapse icon on change of 'key' attribute
    this.collapsedChanged(this.collapsed)
    value = _.toString(value)
    const indent = (value.match(RE_COUNT_INDENTS)||[]).length
    this.indent = indent>0 ? indent : ''
  } // keyChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "key"; same effect as changed language; will refresh list of items
  */
  dataChanged() {
    this.invokeDependentChanges('data')
  } // dataChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "collapsed"
  */
  collapsedChanged(value) {
    const key = _.toString(this.key)
    let elIcon
    const iconOptions = this.indenticon.split(',')

    if(Array.isArray(iconOptions) && iconOptions.length === 2) {
      elIcon = key.charAt(key.length-1)==='>' ? this.getTemplate(value ? iconOptions[0] : iconOptions[1]) : null
    }else {
      elIcon = key.charAt(key.length-1)==='>' ? this.getTemplate(value ? 'sys.plus' : 'sys.minus') : null
    }

    if(!elIcon) {
      if(this._collapseicon) {
        this._collapseicon.deleteElement()
        delete this._collapseicon
      }
    }else{
      if(!this._collapseicon) {
        this._collapseicon = this.createElementFast('mele-icon')
        this._collapseicon.classList.add('collapse')
      }else{
        this._collapseicon.innerHTML = ''
      }
      this._collapseicon.classList.add(this.classScope('icon'))
      this._collapseicon.appendChild(elIcon)
      this.appendChild(this._collapseicon)
    }
  } // collapsedChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "label"
  */
  labelChanged(value) {
    let html = this.translateAndSetDependencies(value, false, 'label')
    if(String(this.key??'')==='' && html==='') html = '-'
    if(html==='') {
      if(!this._labels) return
      if(this._label) {
        this._label.deleteElement()
        delete this._label
      }
      if(this._labels && !this._label2) {
        // delete holder for labels if neither label nor label2 exist
        this._labels.deleteElement()
        delete this._labels
      }
    }else{
      this._createLabel(html)
    }
  } // labelChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "value"
  */
  valueChanged(value) {
    value = _.toString(value)
    this.clearDependencies('value')

    if(value.indexOf('∫')>=0) {
      value = value.split('∫').map(val=>{ return '<span>'+this.translateAndAppendDependencies(val, false, 'value')+'</span>' }).join('')
    }else if(value!=='') {
      value = this.translateAndAppendDependencies(value, false, 'value')
    }

    if(value==='') {
      this.forAll('>mele-value', el=>el.deleteElement())
    }else{
      const el = this.find('>mele-value') || this.appendNewValue()
      if(el.innerHTML !== value) el.innerHTML = value
    }
  } // valueChanged

  appendNewValue() {
    const el = this.appendChildren({ tag: 'mele-value' })
    el.classList.add(this.classScope('value'))
    return el
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "attachments"
  */
  attachmentsChanged(value) {
    value = _.toString(value)
    this.clearDependencies('attachments')

    if(value!=='') {
      value = this.translateAndAppendDependencies(value, false, 'attachments')
      value = value.replace(/<\/span><span>/g, '∫').replace(/^<span>/, '').replace(/<\/span>$/, '')
    }

    if(value==='') {
      this.forAll('>mele-files', el=>el.deleteElement())
    }else{
      const el = this.find('>mele-files') || this.appendChildren({ tag: 'mele-files' })
      if(el.data !== value) el.data = value
    }
  } // attachmentsChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "removable"
  */
  removableChanged(value) {
    if(value && !this._removeicon) {
      const elIcon = this.getTemplate('u.clear')
      this._removeicon = this.prependChildren({ tag: 'mele-icon', class: 'remove' })
      this._removeicon.classList.add(this.classScope('icon'))
      this._removeicon.appendChild(elIcon)
    }else if(!value && this._removeicon) {
      this._removeicon.deleteElement()
      delete this._removeicon
    }
  } // removableChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle event "click"
  */
  onclick(event) {
    if(this.hidden || this.disabled) return

    // find in which part of the item the click happened
    let el = event.target
    while(el!==this && el.parentElement && el.parentElement!==this) { el=el.parentElement }
    const icon_clicked = _.toLower(el.tagName)==='mele-icon' ? el.className : null
    this.clicked(this, icon_clicked)
    event.stopPropagation()
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * This could be overwritten to get information on item's click
  */
  clicked() {
  }
} // class meleItem

meleItem._properties = PROPERTIES
meleItem.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-item', meleItem)
